/**
 * Currency Context
 * Manages merchant's display currency preferences and provides
 * currency formatting/conversion utilities across the app.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSecureAuth } from './SecureAuthContext';

const CurrencyContext = createContext();

// Supported currencies with their metadata
const CURRENCIES = {
  NGN: { code: 'NGN', symbol: '₦', name: 'Nigerian Naira', decimalPlaces: 2 },
  GHS: { code: 'GHS', symbol: 'GH₵', name: 'Ghanaian Cedi', decimalPlaces: 2 },
  KES: { code: 'KES', symbol: 'KSh', name: 'Kenyan Shilling', decimalPlaces: 2 },
  TZS: { code: 'TZS', symbol: 'TSh', name: 'Tanzanian Shilling', decimalPlaces: 0 },
  UGX: { code: 'UGX', symbol: 'USh', name: 'Ugandan Shilling', decimalPlaces: 0 },
  USD: { code: 'USD', symbol: '$', name: 'US Dollar', decimalPlaces: 2 },
  EUR: { code: 'EUR', symbol: '€', name: 'Euro', decimalPlaces: 2 },
  GBP: { code: 'GBP', symbol: '£', name: 'British Pound', decimalPlaces: 2 }
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  // Get authentication state
  const { isAuthenticated, loading: authLoading } = useSecureAuth();

  // Currency state
  const [accountCurrency, setAccountCurrency] = useState('NGN'); // Merchant's base/local currency
  const [displayCurrency, setDisplayCurrency] = useState('NGN'); // Current display preference
  const [allowedCurrencies, setAllowedCurrencies] = useState(['USD']);
  const [exchangeRates, setExchangeRates] = useState({ NGN: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  /**
   * Fetch currency settings from backend
   * Only fetches if authenticated
   */
  const fetchCurrencySettings = useCallback(async () => {
    // Don't fetch if not authenticated
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch('/api/merchant/auth/currency', {
        credentials: 'include'
      });

      if (!response.ok) {
        // Handle 401/403 silently - user might not be authenticated yet
        if (response.status === 401 || response.status === 403) {
          // Use defaults silently - this is expected during auth transitions
          setLoading(false);
          return;
        }
        throw new Error('Failed to fetch currency settings');
      }

      const data = await response.json();

      if (data.success && data.currency) {
        setAccountCurrency(data.currency.accountCurrency || 'NGN');
        setDisplayCurrency(data.currency.displayCurrency || data.currency.accountCurrency || 'NGN');
        setAllowedCurrencies(data.currency.allowedCurrencies || ['USD']);
        setExchangeRates(data.currency.exchangeRates || { NGN: 1 });
      }

      setError(null);
      setHasFetched(true);
    } catch (err) {
      console.error('[CurrencyContext] Error fetching settings:', err);
      setError(err.message);
      // Use defaults on error
      setAccountCurrency('NGN');
      setDisplayCurrency('NGN');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  /**
   * Switch the display currency
   */
  const switchCurrency = useCallback(async (newCurrency) => {
    try {
      const response = await fetch('/api/merchant/auth/currency/switch', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ currency: newCurrency })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to switch currency');
      }

      const data = await response.json();

      if (data.success) {
        setDisplayCurrency(newCurrency);
        return { success: true };
      }

      throw new Error(data.error || 'Failed to switch currency');
    } catch (err) {
      console.error('[CurrencyContext] Error switching currency:', err);
      return { success: false, error: err.message };
    }
  }, []);

  /**
   * Convert amount from one currency to another
   */
  const convertAmount = useCallback((amount, fromCurrency, toCurrency) => {
    if (!amount || isNaN(amount)) return 0;
    if (fromCurrency === toCurrency) return parseFloat(amount);

    const fromRate = exchangeRates[fromCurrency] || 1;
    const toRate = exchangeRates[toCurrency] || 1;

    // Convert through NGN as base
    // fromRate is: 1 NGN = X fromCurrency
    // toRate is: 1 NGN = X toCurrency
    // To convert: amount / fromRate * toRate
    const amountInNGN = parseFloat(amount) / fromRate;
    const converted = amountInNGN * toRate;

    // Round based on target currency decimals
    const decimals = CURRENCIES[toCurrency]?.decimalPlaces ?? 2;
    return Math.round(converted * Math.pow(10, decimals)) / Math.pow(10, decimals);
  }, [exchangeRates]);

  /**
   * Format money with currency symbol
   */
  const formatMoney = useCallback((amount, currency = null, options = {}) => {
    const targetCurrency = currency || displayCurrency;
    const currencyInfo = CURRENCIES[targetCurrency] || CURRENCIES.NGN;
    const { compact = false, showCode = false } = options;

    let value = parseFloat(amount || 0);

    // Format number
    let formattedAmount;
    if (compact && Math.abs(value) >= 1000000) {
      formattedAmount = (value / 1000000).toFixed(1) + 'M';
    } else if (compact && Math.abs(value) >= 1000) {
      formattedAmount = (value / 1000).toFixed(1) + 'K';
    } else {
      formattedAmount = value.toLocaleString('en-US', {
        minimumFractionDigits: currencyInfo.decimalPlaces,
        maximumFractionDigits: currencyInfo.decimalPlaces
      });
    }

    const result = `${currencyInfo.symbol}${formattedAmount}`;
    return showCode ? `${result} ${currencyInfo.code}` : result;
  }, [displayCurrency]);

  /**
   * Format money in display currency with automatic conversion
   * If amount is in account currency, converts to display currency
   */
  const formatInDisplayCurrency = useCallback((amount, sourceCurrency = null) => {
    const source = sourceCurrency || accountCurrency;

    if (source === displayCurrency) {
      return formatMoney(amount, displayCurrency);
    }

    const converted = convertAmount(amount, source, displayCurrency);
    return formatMoney(converted, displayCurrency);
  }, [accountCurrency, displayCurrency, convertAmount, formatMoney]);

  /**
   * Format money with dual display (original + converted)
   * Shows amount in original currency with equivalent in display currency
   */
  const formatDualCurrency = useCallback((amount, originalCurrency) => {
    const original = formatMoney(amount, originalCurrency);

    if (originalCurrency === displayCurrency) {
      return original;
    }

    const converted = convertAmount(amount, originalCurrency, displayCurrency);
    const convertedFormatted = formatMoney(converted, displayCurrency);

    return `${original} (~${convertedFormatted})`;
  }, [displayCurrency, convertAmount, formatMoney]);

  /**
   * Get currency info
   */
  const getCurrencyInfo = useCallback((currencyCode) => {
    return CURRENCIES[currencyCode] || null;
  }, []);

  /**
   * Get available display currencies for this merchant
   * Always includes account currency + allowed currencies
   */
  const getAvailableCurrencies = useCallback(() => {
    const currencies = new Set([accountCurrency, ...allowedCurrencies]);
    return Array.from(currencies).map(code => ({
      code,
      ...CURRENCIES[code]
    })).filter(c => c.symbol); // Filter out any invalid codes
  }, [accountCurrency, allowedCurrencies]);

  // Fetch settings when authentication completes
  useEffect(() => {
    // Only fetch if:
    // 1. Auth is not loading anymore
    // 2. User is authenticated
    // 3. We haven't fetched yet
    if (!authLoading && isAuthenticated && !hasFetched) {
      fetchCurrencySettings();
    }

    // If auth completes and user is not authenticated, stop loading
    if (!authLoading && !isAuthenticated) {
      setLoading(false);
    }
  }, [authLoading, isAuthenticated, hasFetched, fetchCurrencySettings]);

  const value = {
    // State
    accountCurrency,
    displayCurrency,
    allowedCurrencies,
    exchangeRates,
    loading,
    error,

    // Functions
    switchCurrency,
    convertAmount,
    formatMoney,
    formatInDisplayCurrency,
    formatDualCurrency,
    getCurrencyInfo,
    getAvailableCurrencies,
    refreshSettings: fetchCurrencySettings,

    // Constants
    CURRENCIES
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export default CurrencyContext;
